/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input } from "rizzui";
import { DatePicker } from "@/components/ui/datepicker";
import toast from "react-hot-toast";
import useApi from "@/hooks/useApi";
import AdCreditRepository from "@/utilities/repositories/AdCredit";
import { useParams } from "react-router-dom";
import useApiStore from "@/hooks/useApiStore";

const updateUserCreditSchema = z.object({
  packageName: z.string().optional(),
  packageExpiryDate: z.date().optional(),
  creditPrice: z
    .string()
    .min(1, "Credit price is required")
    .regex(/^\d+(\.\d{1,2})?$/, "Please enter a valid number"),
});

type UpdateUserCreditFormData = z.infer<typeof updateUserCreditSchema>;

interface UpdateUserCreditFormProps {
  onSuccess?: () => void;
}

export default function UpdateUserCreditPackageForm({
  onSuccess,
}: UpdateUserCreditFormProps) {
  const { id: userId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<UpdateUserCreditFormData>({
    resolver: zodResolver(updateUserCreditSchema),
  });

  const { request: getAdCreditPerUser, response: userAdCreditData } = useApi({
    request: AdCreditRepository.getUserAdCreditByAdmin,
    enableToast: false,
  });

  const requestUserAdCreditHistory =
    userId &&
    useApiStore({
      request: AdCreditRepository.getUserAdCreditCollapseHistoryByAdmin({
        userId,
      }),
      enableToast: false,
      key: "ad-credit-transaction",
    });

  const fetchAdCreditPackage = async () => {
    const userAdCreditData = await getAdCreditPerUser(userId);
    const userAdCredit = userAdCreditData?.data[0];
    if (userAdCredit) {
      const { name, expiredAt, price } = userAdCredit;
      reset({
        packageName: name,
        packageExpiryDate: expiredAt ? new Date(expiredAt) : undefined,
        creditPrice: price.toString(),
      });
    }
  };

  const onSubmit = async (data: UpdateUserCreditFormData) => {
    if (!userId) {
      toast.error("User ID is required");
      return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading("Updating user credit...");

    try {
      if (userAdCreditData?.data[0]?.id) {
        await AdCreditRepository.updatedCreditPackage(
          userAdCreditData?.data[0]?.id,
          {
            name: data.packageName,
            expiredAt: data.packageExpiryDate?.toISOString(),
            price: parseFloat(data.creditPrice),
            amount: 1,
          },
        );
      } else
        await AdCreditRepository.createAdCreditPackage({
          assignUserId: userId,
          name: data.packageName || "",
          expiredAt: data.packageExpiryDate?.toISOString(),
          price: parseFloat(data.creditPrice),
          amount: 1,
        });
      await fetchAdCreditPackage();
      toast.success("User credit updated successfully", { id: toastId });
      reset();
      onSuccess?.();
      requestUserAdCreditHistory && requestUserAdCreditHistory?.refetch();
    } catch (error: any) {
      toast.error(error?.message || "Failed to update user credit", {
        id: toastId,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedDate = watch("packageExpiryDate");

  useEffect(() => {
    (async () => {
      await fetchAdCreditPackage();
    })();
  }, [userId]);

  return (
    <div className="pt-4">
      <div className="space-y-6">
        {/* Package Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Package Name
          </label>
          <Input
            {...register("packageName")}
            placeholder="Enter package name"
            className="w-full"
            error={errors.packageName?.message}
          />
        </div>

        {/* Package Expiry Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Package Expiry Date (Optional)
          </label>
          <div className="relative">
            <DatePicker
              showYearPickerHeader
              selected={selectedDate}
              onChange={(date: Date | null) => {
                setValue("packageExpiryDate", date || undefined);
              }}
              dateFormat="dd/MM/yyyy"
              minDate={new Date()}
              isClearable
              inputProps={{
                placeholder: "Select expiry date (optional)",
                className: "w-full",
                error: errors.packageExpiryDate?.message,
              }}
            />
          </div>
        </div>

        {/* Credit Price */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Credit Price
          </label>
          <Input
            {...register("creditPrice")}
            placeholder="Only numbers"
            className="w-full"
            error={errors.creditPrice?.message}
            type="text"
          />
        </div>

        {/* Save Button */}
        <div>
          <Button
            type="button"
            className="bg-black text-white hover:bg-gray-800 px-8 py-2 rounded-md"
            disabled={isSubmitting}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(onSubmit)();
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
